import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Upload, Camera, MessageSquare, FileText, Shield, ShieldAlert, ShieldCheck, History, Trash2, AlertTriangle, Eye, EyeOff, Download, FileEdit, Lock, Play, Pause, X } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { api } from '@/lib/api';
import { tfChunkedUpload, tfAbandonUpload } from '@/lib/tfChunkedUpload';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import i18next from 'i18next';

// UAT #3 — ES/EN localization helper (file was English-only).
const L = (en, es) => ((i18next.language || 'es').startsWith('es') ? es : en);
import { EvidenceKindBadge } from '@/components/talentfix/EvidenceKindBadge';
import { extractExtension, FileTypeIcon, FileExtensionBadge, formatFileSize } from '@/components/talentfix/EvidenceFileMeta';

const CHUNKED_THRESHOLD_BYTES = 25 * 1024 * 1024; // > 25 MB or audio/video → chunked
const CHUNKED_KINDS = new Set(['audio', 'video']);

function shouldUseChunked(kind, file) {
  if (!file) return false;
  if (CHUNKED_KINDS.has(kind)) return true;
  return file.size > CHUNKED_THRESHOLD_BYTES;
}

function humanBytes(n) {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

const LEVEL_TONE = {
  client_visible: 'bg-emerald-100 text-emerald-700',
  shared_with_client: 'bg-teal-100 text-teal-700',
  restricted: 'bg-slate-100 text-slate-700',
  privileged: 'bg-amber-100 text-amber-700',
  legal_hold: 'bg-rose-100 text-rose-700',
};

const FAMILY_LABEL = {
  investigation_artefact: 'Investigation',
  document: 'Documents',
  media: 'Media',
  communication: 'Communication',
  testimony: 'Testimony',
  snapshot: 'Snapshots',
  external: 'External',
};

// ===== Upload dialog =====
function UploadDialog({ open, onOpenChange, caseId, onUploaded }) {
  const [kind, setKind] = useState('document_pdf');
  const [title, setTitle] = useState('');
  const [conf, setConf] = useState('restricted');
  const [file, setFile] = useState(null);
  const [kindDesc, setKindDesc] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(null); // {percent, phase, received, total} | null
  const abortRef = useRef(null);
  useEffect(() => { if (open) { setKind('document_pdf'); setTitle(''); setConf('restricted'); setFile(null); setKindDesc(''); setProgress(null); abortRef.current = null; } }, [open]);
  const submit = async () => {
    if (!file) { toast.error(L('Pick a file','Selecciona un archivo')); return; }
    if (title.trim().length < 2) { toast.error(L('Title required','El título es obligatorio')); return; }
    if (kind === 'other' && (kindDesc || '').trim().length < 5) { toast.error(L('Description required (5+ chars)','Descripción obligatoria (5+ caracteres)')); return; }
    setSubmitting(true);
    try {
      if (shouldUseChunked(kind, file)) {
        abortRef.current = new AbortController();
        await tfChunkedUpload({
          caseId,
          file,
          manifest: {
            kind,
            title: title.trim(),
            confidentiality_treatment: conf,
            kind_description: kindDesc || undefined,
          },
          onProgress: (p) => setProgress(p),
          signal: abortRef.current.signal,
        });
      } else {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('kind', kind);
        fd.append('title', title.trim());
        fd.append('confidentiality_treatment', conf);
        if (kindDesc) fd.append('kind_description', kindDesc);
        await adminApi.talentFixUploadEvidence(caseId, fd);
      }
      toast.success(L('Evidence uploaded','Evidencia cargada'));
      onOpenChange(false); onUploaded?.();
    } catch (e) {
      if (e?.name === 'AbortError') {
        toast.message(L('Upload cancelled','Carga cancelada'));
      } else {
        toast.error(e?.response?.data?.detail || e?.message || 'Upload failed');
      }
    } finally { setSubmitting(false); setProgress(null); abortRef.current = null; }
  };
  const cancelUpload = () => {
    if (abortRef.current) abortRef.current.abort();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && submitting) cancelUpload(); onOpenChange(o); }}>
      <DialogContent data-testid="tf4-upload-dialog">
        <DialogHeader><DialogTitle>{L('Upload evidence','Cargar evidencia')}</DialogTitle><DialogDescription>{L('Default confidentiality: restricted. Files > 25 MB and all audio/video use chunked resumable upload.','Confidencialidad predeterminada: restringida. Los archivos > 25 MB y todo audio/video usan carga reanudable por fragmentos.')}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Kind</label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="document_pdf">{L('Document — PDF','Documento — PDF')}</SelectItem>
                <SelectItem value="document_docx">{L('Document — Word','Documento — Word')}</SelectItem>
                <SelectItem value="document_xlsx">{L('Document — Excel','Documento — Excel')}</SelectItem>
                <SelectItem value="image">{L('Image','Imagen')}</SelectItem>
                <SelectItem value="audio">Audio</SelectItem>
                <SelectItem value="video">Video</SelectItem>
                <SelectItem value="email_thread">{L('Email (.eml)','Correo (.eml)')}</SelectItem>
                <SelectItem value="other">{L('Other (requires description)','Otro (requiere descripción)')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><label className="text-xs font-medium text-slate-600">{L('Title','Título')}</label>
            <Input data-testid="tf4-upload-title" value={title} onChange={(e)=>setTitle(e.target.value)} /></div>
          {kind === 'other' && (
            <div><label className="text-xs font-medium text-slate-600">{L('Description (5-250 chars)','Descripción (5-250 caracteres)')}</label>
              <Input value={kindDesc} onChange={(e)=>setKindDesc(e.target.value)} maxLength={250} /></div>
          )}
          <div><label className="text-xs font-medium text-slate-600">File</label>
            <Input type="file" data-testid="tf4-upload-file" onChange={(e)=>setFile(e.target.files?.[0]||null)} /></div>
          {file && shouldUseChunked(kind, file) && (
            <p className="text-xs text-emerald-700" data-testid="tf4-upload-chunked-notice">
              Chunked upload will be used ({humanBytes(file.size)} · 8 MB chunks · resumable).
            </p>
          )}
          <div>
            <label className="text-xs font-medium text-slate-600">{L('Confidentiality','Confidencialidad')}</label>
            <Select value={conf} onValueChange={setConf}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="restricted">{L('Restricted (HM only) — default','Restringida (solo CTH) — predeterminada')}</SelectItem>
                <SelectItem value="shared_with_client">{L('Shared with client','Compartida con el cliente')}</SelectItem>
                <SelectItem value="client_visible">{L('Client visible','Visible para el cliente')}</SelectItem>
                <SelectItem value="privileged">Privileged (lead + admin)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {progress && (
            <div className="space-y-1" data-testid="tf4-upload-progress">
              <div className="flex items-center justify-between text-xs text-slate-600">
                <span className="capitalize">{progress.phase}…</span>
                <span>{progress.percent}%{progress.totalBytes ? ` · ${humanBytes(progress.receivedBytes)} / ${humanBytes(progress.totalBytes)}` : ''}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded bg-slate-200">
                <div
                  data-testid="tf4-upload-progress-bar"
                  className="h-full bg-emerald-500 transition-all"
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          {submitting ? (
            <Button variant="outline" onClick={cancelUpload} data-testid="tf4-upload-cancel">
              <X className="mr-1 h-4 w-4" />Cancel
            </Button>
          ) : (
            <Button variant="outline" onClick={()=>onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          )}
          <Button onClick={submit} disabled={submitting} data-testid="tf4-upload-submit">{submitting?(progress?`Uploading ${progress.percent}%`:'Uploading…'):'Upload'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== Snapshot capture =====
function SnapshotDialog({ open, onOpenChange, caseId, onCaptured }) {
  const [suite, setSuite] = useState('talentcompetency');
  const [title, setTitle] = useState('');
  const [label, setLabel] = useState('');
  const [payload, setPayload] = useState('{\n  "metric": "value"\n}');
  const [conf, setConf] = useState('restricted');
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (title.trim().length < 2) { toast.error(L('Title required','El título es obligatorio')); return; }
    if (label.trim().length < 2) { toast.error(L('Source label required','La etiqueta de origen es obligatoria')); return; }
    let parsedPayload;
    try { parsedPayload = JSON.parse(payload); } catch { toast.error(L('Invalid JSON payload','Payload JSON inválido')); return; }
    setSubmitting(true);
    try {
      await adminApi.talentFixCaptureSnapshot(caseId, {
        source_suite: suite,
        title: title.trim(),
        snapshot_source_label: label.trim(),
        payload: parsedPayload,
        confidentiality_treatment: conf,
      });
      toast.success(L('Snapshot captured (frozen)','Snapshot capturado (congelado)'));
      onOpenChange(false); onCaptured?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Capture failed');
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="tf4-snapshot-dialog">
        <DialogHeader><DialogTitle>{L('Capture sibling-suite snapshot','Capturar snapshot de otra suite')}</DialogTitle>
          <DialogDescription>Frozen + locked at capture. Cannot be edited or versioned later.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-medium text-slate-600">{L('Source suite','Suite de origen')}</label>
            <Select value={suite} onValueChange={setSuite}>
              <SelectTrigger data-testid="tf4-snap-suite"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="talentcheck">TalentCheck</SelectItem>
                <SelectItem value="talentcompetency">TalentCompetency</SelectItem>
                <SelectItem value="talentmap">TalentMap</SelectItem>
                <SelectItem value="talentheart">TalentHeart</SelectItem>
                <SelectItem value="climara">Climara</SelectItem>
                <SelectItem value="intelligence">Talent Intelligence</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><label className="text-xs font-medium text-slate-600">{L('Title','Título')}</label>
            <Input value={title} onChange={(e)=>setTitle(e.target.value)} /></div>
          <div><label className="text-xs font-medium text-slate-600">Source label (e.g. &quot;User Pablo Q2 2026 cycle&quot;)</label>
            <Input value={label} onChange={(e)=>setLabel(e.target.value)} /></div>
          <div><label className="text-xs font-medium text-slate-600">{L('Payload (JSON — captured verbatim)','Payload (JSON — capturado literalmente)')}</label>
            <Textarea rows={6} className="font-mono text-xs" value={payload} onChange={(e)=>setPayload(e.target.value)} /></div>
          <div><label className="text-xs font-medium text-slate-600">{L('Confidentiality','Confidencialidad')}</label>
            <Select value={conf} onValueChange={setConf}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="restricted">{L('Restricted','Restringida')}</SelectItem>
                <SelectItem value="client_visible">{L('Client visible','Visible para el cliente')}</SelectItem>
                <SelectItem value="shared_with_client">{L('Shared with client','Compartida con el cliente')}</SelectItem>
                <SelectItem value="privileged">{L('Privileged','Privilegiada')}</SelectItem>
              </SelectContent>
            </Select></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          <Button onClick={submit} disabled={submitting} data-testid="tf4-snap-submit">{submitting?'Capturing…':'Capture (freeze)'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== Witness statement =====
function WitnessDialog({ open, onOpenChange, caseId, onCaptured }) {
  const [form, setForm] = useState({ title:'', witness_label:'', witness_role:'',
    witness_relationship_to_case:'witness', witness_is_anonymous:false,
    statement_body:'', confidentiality_treatment:'restricted' });
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (form.title.trim().length < 2) return toast.error(L('Title required','El título es obligatorio'));
    if (form.witness_label.trim().length < 5) return toast.error(L('Witness label ≥ 5 chars','Etiqueta del testigo: mínimo 5 caracteres'));
    if (!(form.statement_body||'').trim()) return toast.error(L('Statement body required','El cuerpo de la declaración es obligatorio'));
    setSubmitting(true);
    try {
      await adminApi.talentFixCaptureWitness(caseId, form);
      toast.success(L('Witness statement captured','Declaración de testigo registrada'));
      onOpenChange(false); onCaptured?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="tf4-witness-dialog">
        <DialogHeader><DialogTitle>{L('Add witness statement','Agregar declaración de testigo')}</DialogTitle>
          <DialogDescription>{L('Written declarations outside the formal Interview workflow.','Declaraciones escritas fuera del flujo formal de entrevistas.')}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-medium text-slate-600">{L('Title','Título')}</label>
            <Input value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})}/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-slate-600">{L('Witness label','Etiqueta del testigo')}</label>
              <Input value={form.witness_label} onChange={(e)=>setForm({...form,witness_label:e.target.value})}/></div>
            <div><label className="text-xs font-medium text-slate-600">{L('Role (hidden if anonymous)','Rol (oculto si es anónimo)')}</label>
              <Input value={form.witness_role} disabled={form.witness_is_anonymous}
                     onChange={(e)=>setForm({...form,witness_role:e.target.value})}/></div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="anon" data-testid="tf4-witness-anon" checked={form.witness_is_anonymous}
              onChange={(e)=>setForm({...form,witness_is_anonymous:e.target.checked})}/>
            <label htmlFor="anon" className="text-sm">{L('Anonymous (strips role server-side)','Anónimo (el rol se elimina en el servidor)')}</label>
          </div>
          <div><label className="text-xs font-medium text-slate-600">{L('Relationship','Relación')}</label>
            <Select value={form.witness_relationship_to_case}
              onValueChange={(v)=>setForm({...form,witness_relationship_to_case:v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="subject">{L('Subject','Sujeto')}</SelectItem>
                <SelectItem value="complainant">{L('Complainant','Denunciante')}</SelectItem>
                <SelectItem value="witness">{L('Witness','Testigo')}</SelectItem>
                <SelectItem value="third_party">{L('Third party','Tercero')}</SelectItem>
                <SelectItem value="other">{L('Other','Otro')}</SelectItem>
              </SelectContent>
            </Select></div>
          <div><label className="text-xs font-medium text-slate-600">{L('Statement body','Cuerpo de la declaración')}</label>
            <Textarea rows={6} value={form.statement_body}
              onChange={(e)=>setForm({...form,statement_body:e.target.value})}/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          <Button onClick={submit} disabled={submitting} data-testid="tf4-witness-submit">{submitting?'Saving…':'Add'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== Confidentiality change dialog =====
function ConfDialog({ open, onOpenChange, item, isHmAdmin, onChanged }) {
  const [to, setTo] = useState('restricted');
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (open && item) { setTo(item.confidentiality_treatment || 'restricted'); setRationale(''); } }, [open, item]);
  if (!item) return null;
  const isLegalHold = item.confidentiality_treatment === 'legal_hold' || to === 'legal_hold';
  const minRat = isLegalHold ? 20 : 5;
  const submit = async () => {
    if ((rationale||'').trim().length < minRat) return toast.error(`Rationale must be ≥ ${minRat} chars`);
    setSubmitting(true);
    try {
      if (item.confidentiality_treatment === 'legal_hold') {
        await adminApi.talentFixReleaseLegalHold(item.id, { to_level: to, rationale });
      } else {
        await adminApi.talentFixChangeConfidentiality(item.id, { to_level: to, rationale });
      }
      toast.success(L('Confidentiality updated','Confidencialidad actualizada'));
      onOpenChange(false); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Change failed');
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf4-conf-dialog">
        <DialogHeader>
          <DialogTitle>{L('Change confidentiality','Cambiar confidencialidad')}</DialogTitle>
          <DialogDescription>{L('Current','Actual')}: <code>{item.confidentiality_treatment}</code> · {L('Title','Título')}: {item.title}</DialogDescription>
        </DialogHeader>
        {(item.confidentiality_treatment === 'legal_hold' || to === 'legal_hold') && !isHmAdmin && (
          <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            <Lock className="-mt-0.5 mr-1 inline h-3.5 w-3.5" />
            Legal-hold transitions require HM Admin (Adj-3).
          </div>
        )}
        <div className="space-y-3">
          <div><label className="text-xs font-medium text-slate-600">{L('New level','Nuevo nivel')}</label>
            <Select value={to} onValueChange={setTo}>
              <SelectTrigger data-testid="tf4-conf-to"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="client_visible">{L('Client visible','Visible para el cliente')}</SelectItem>
                <SelectItem value="shared_with_client">{L('Shared with client','Compartida con el cliente')}</SelectItem>
                <SelectItem value="restricted">{L('Restricted','Restringida')}</SelectItem>
                <SelectItem value="privileged">{L('Privileged','Privilegiada')}</SelectItem>
                {isHmAdmin && <SelectItem value="legal_hold">{L('Legal Hold (HM Admin only)','Retención legal (solo HM Admin)')}</SelectItem>}
              </SelectContent>
            </Select></div>
          <div><label className="text-xs font-medium text-slate-600">
            Rationale ({minRat}+ chars){isLegalHold && ' — REQUIRED for legal-hold transitions'}
          </label>
            <Textarea rows={3} value={rationale} onChange={(e)=>setRationale(e.target.value)}
              data-testid="tf4-conf-rationale" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          <Button onClick={submit} disabled={submitting} data-testid="tf4-conf-submit">{submitting?'Saving…':'Apply'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== Media preview block (inline <audio>/<video>/<img>) =====
function MediaPreview({ ev, basePath }) {
  if (!ev) return null;
  const url = `${api.defaults.baseURL.replace(/\/$/, '')}${basePath}/${ev.id}/download.v2`;
  const family = ev.family;
  const kind = ev.kind;
  if (family === 'media' && kind === 'video') {
    const thumb = ev.thumbnail_uri
      ? `${api.defaults.baseURL.replace(/\/$/, '')}${basePath}/${ev.id}/thumbnail`
      : undefined;
    return (
      <video
        data-testid={`tf4-video-preview-${ev.id}`}
        controls
        preload="metadata"
        poster={thumb}
        className="w-full max-h-96 rounded border border-slate-200 bg-black"
        src={url}
      />
    );
  }
  if (family === 'media' && kind === 'audio') {
    return (
      <audio
        data-testid={`tf4-audio-preview-${ev.id}`}
        controls
        preload="metadata"
        className="w-full"
        src={url}
      />
    );
  }
  if (family === 'media' && kind === 'image') {
    return (
      <img
        data-testid={`tf4-image-preview-${ev.id}`}
        src={url}
        alt={ev.title}
        className="max-h-96 cursor-zoom-in rounded border border-slate-200 object-contain"
        onClick={(e) => {
          // open in new tab for full size
          window.open(url, '_blank');
          e.stopPropagation();
        }}
      />
    );
  }
  return null;
}

// ===== Evidence card =====
function EvidenceCard({ ev, onChangeConf, onSoftDelete, onNewVersion, onViewLineage }) {
  const isLocked = ev.is_locked || ev.is_frozen_snapshot;
  const isLegalHold = ev.confidentiality_treatment === 'legal_hold';
  const isWitness = ev.kind === 'witness_statement';
  const isMedia = ev.family === 'media' && ['video', 'audio', 'image'].includes(ev.kind);
  const [previewOpen, setPreviewOpen] = useState(false);
  const thumbUrl = ev.thumbnail_uri
    ? `${api.defaults.baseURL.replace(/\/$/, '')}/admin/talentfix/evidence/${ev.id}/thumbnail`
    : null;
  return (
    <Card data-testid={`tf4-evidence-card-${ev.id}`} className="p-3">
      <div className="flex items-start justify-between gap-3">
        {thumbUrl && (
          <img
            data-testid={`tf4-evidence-thumb-${ev.id}`}
            src={thumbUrl}
            alt=""
            className="h-16 w-24 shrink-0 rounded border border-slate-200 object-cover"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            {(() => {
              const ext = extractExtension(ev.title) || extractExtension(ev.attachment_uri);
              const downloadUrl = ev.attachment_uri
                ? `${api.defaults.baseURL.replace(/\/$/,'')}/admin/talentfix/evidence/${ev.id}/download`
                : null;
              return (
                <>
                  {isLocked
                    ? <Lock className="h-4 w-4 text-blue-600" />
                    : <FileTypeIcon ext={ext} className="h-4 w-4 text-slate-500" />}
                  {downloadUrl ? (
                    <a href={downloadUrl} target="_blank" rel="noreferrer"
                       data-testid={`tf4-filename-link-${ev.id}`}
                       className="text-sm font-medium text-slate-800 hover:text-blue-700 hover:underline truncate max-w-[420px]">
                      {ev.title}
                    </a>
                  ) : (
                    <span data-testid={`tf4-filename-${ev.id}`}
                          className="text-sm font-medium text-slate-800 truncate max-w-[420px]">
                      {ev.title}
                    </span>
                  )}
                  {ext && <FileExtensionBadge ext={ext} />}
                  <EvidenceKindBadge kind={ev.kind} />
                </>
              );
            })()}
            <Badge className={LEVEL_TONE[ev.confidentiality_treatment] || 'bg-slate-100'}>
              {(ev.confidentiality_treatment || '').replace(/_/g, ' ')}
            </Badge>
            {ev.version_number > 1 && (
              <Badge variant="outline" data-testid={`tf4-version-badge-${ev.id}`}>v{ev.version_number}</Badge>
            )}
            {ev.is_frozen_snapshot && <Badge className="bg-blue-100 text-blue-700">{L('frozen','congelado')}</Badge>}
            {isWitness && ev.witness_is_anonymous && <Badge className="bg-purple-100 text-purple-700">{L('anonymous','anónimo')}</Badge>}
            {ev.upload_method === 'chunked' && <Badge variant="outline" className="text-emerald-700 border-emerald-200">chunked</Badge>}
          </div>
          <p className="mt-0.5 text-xs text-slate-500 capitalize">
            {FAMILY_LABEL[ev.family] || ev.family || 'evidence'} · {(ev.kind || '').replace(/_/g, ' ')}
            {ev.media_duration_seconds && (
              <span className="ml-2 text-slate-400">· {Math.round(ev.media_duration_seconds)}s</span>
            )}
            {ev.media_width && ev.media_height && (
              <span className="ml-2 text-slate-400">· {ev.media_width}×{ev.media_height}</span>
            )}
          </p>
          {ev.attachment_size_bytes && (
            <p className="text-xs text-slate-500" data-testid={`tf4-filesize-${ev.id}`}>
              {formatFileSize(ev.attachment_size_bytes)} · sha {(ev.attachment_sha256 || '').slice(0,8)}…
            </p>
          )}
          {ev.snapshot_source_suite && (
            <p className="text-xs text-slate-500">
              snapshot: {ev.snapshot_source_suite} · sha {(ev.snapshot_content_sha256 || '').slice(0,8)}…
            </p>
          )}
          {isWitness && ev.witness_label && (
            <p className="text-xs text-slate-500">witness: {ev.witness_label}</p>
          )}
          {ev.first_shared_at && (
            <p className="text-xs text-emerald-700">
              <ShieldCheck className="inline h-3 w-3 mr-1"/>shared with client by {ev.first_shared_by_name}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          {isMedia && (
            <Button size="sm" variant="outline"
              onClick={() => setPreviewOpen((p) => !p)}
              data-testid={`tf4-preview-btn-${ev.id}`}>
              {previewOpen ? <><EyeOff className="h-3.5 w-3.5 mr-1"/>{L('Hide','Ocultar')}</> : <><Play className="h-3.5 w-3.5 mr-1"/>{L('Preview','Vista previa')}</>}
            </Button>
          )}
          {ev.attachment_uri && (
            <a href={`${api.defaults.baseURL.replace(/\/$/,'')}/admin/talentfix/evidence/${ev.id}/download`}
               target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline"><Download className="h-3.5 w-3.5 mr-1"/>{L('Download','Descargar')}</Button>
            </a>
          )}
          <Button size="sm" variant="outline" onClick={() => onChangeConf(ev)}
            data-testid={`tf4-conf-btn-${ev.id}`}><Shield className="h-3.5 w-3.5 mr-1"/>{L('Confidentiality','Confidencialidad')}</Button>
          {!isLocked && !isLegalHold && (
            <Button size="sm" variant="outline" onClick={() => onNewVersion(ev)}
              data-testid={`tf4-newver-btn-${ev.id}`}><FileEdit className="h-3.5 w-3.5 mr-1"/>{L('New version','Nueva versión')}</Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onViewLineage(ev)}
            data-testid={`tf4-lineage-btn-${ev.id}`}><History className="h-3.5 w-3.5 mr-1"/>{L('History','Historial')}</Button>
          {!isLegalHold && (
            <Button size="sm" variant="outline" onClick={() => onSoftDelete(ev)}
              data-testid={`tf4-delete-btn-${ev.id}`} className="text-rose-700">
              <Trash2 className="h-3.5 w-3.5 mr-1"/>Delete
            </Button>
          )}
        </div>
      </div>
      {previewOpen && isMedia && (
        <div className="mt-3" data-testid={`tf4-preview-block-${ev.id}`}>
          <MediaPreview ev={ev} basePath="/admin/talentfix/evidence" />
        </div>
      )}
    </Card>
  );
}

// ===== Lineage panel =====
function LineageDialog({ open, onOpenChange, evidence }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (open && evidence) {
      adminApi.talentFixGetEvidenceLineage(evidence.id).then(setData).catch(()=>setData({versions:[]}));
    }
  }, [open, evidence]);
  if (!evidence) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf4-lineage-dialog">
        <DialogHeader><DialogTitle>Version history — {evidence.title}</DialogTitle>
          <DialogDescription>{L('All versions sorted chronologically.','Todas las versiones en orden cronológico.')}</DialogDescription></DialogHeader>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {(data?.versions || []).map((v) => (
            <div key={v.id} className={`rounded border px-3 py-2 ${v.is_latest_version ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">v{v.version_number}{v.is_latest_version && ' · latest'}</span>
                <span className="text-xs text-slate-500">{String(v.created_at).slice(0,16).replace('T',' ')}</span>
              </div>
              <p className="text-xs text-slate-500">{v.title}</p>
              {v.attachment_size_bytes && (
                <p className="text-xs text-slate-500">{Math.round(v.attachment_size_bytes/1024)} KB · sha {(v.attachment_sha256||'').slice(0,8)}…</p>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== New version dialog =====
function NewVersionDialog({ open, onOpenChange, evidence, onUploaded }) {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(null);
  const abortRef = useRef(null);
  useEffect(() => { if (open) { setFile(null); setNote(''); setProgress(null); abortRef.current = null; } }, [open]);
  if (!evidence) return null;
  const cancelUpload = () => { if (abortRef.current) abortRef.current.abort(); };
  const submit = async () => {
    if (!file) return toast.error(L('Pick a file','Selecciona un archivo'));
    setSubmitting(true);
    try {
      if (shouldUseChunked(evidence.kind, file)) {
        abortRef.current = new AbortController();
        await tfChunkedUpload({
          caseId: evidence.case_id,
          file,
          manifest: {
            kind: evidence.kind,
            title: evidence.title,
            confidentiality_treatment: evidence.confidentiality_treatment,
            parent_evidence_id: evidence.id,
            change_note: note || undefined,
          },
          onProgress: setProgress,
          signal: abortRef.current.signal,
        });
      } else {
        const fd = new FormData();
        fd.append('file', file);
        if (note) fd.append('change_note', note);
        await adminApi.talentFixNewVersion(evidence.id, fd);
      }
      toast.success(L('New version uploaded','Nueva versión cargada'));
      onOpenChange(false); onUploaded?.();
    } catch (e) {
      if (e?.name === 'AbortError') toast.message(L('Upload cancelled','Carga cancelada'));
      else toast.error(e?.response?.data?.detail || e?.message || 'Failed');
    } finally { setSubmitting(false); setProgress(null); abortRef.current = null; }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && submitting) cancelUpload(); onOpenChange(o); }}>
      <DialogContent data-testid="tf4-newver-dialog">
        <DialogHeader><DialogTitle>{L('Upload new version','Cargar nueva versión')}</DialogTitle>
          <DialogDescription>Parent: {evidence.title} (current v{evidence.version_number}). New row will become v{evidence.version_number + 1}.</DialogDescription></DialogHeader>
        <Input type="file" onChange={(e)=>setFile(e.target.files?.[0]||null)} data-testid="tf4-newver-file"/>
        {file && shouldUseChunked(evidence.kind, file) && (
          <p className="text-xs text-emerald-700">Chunked upload will be used ({humanBytes(file.size)}).</p>
        )}
        <Textarea rows={2} placeholder={L('Change note (optional)','Nota de cambio (opcional)')} value={note} onChange={(e)=>setNote(e.target.value)}/>
        {progress && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs text-slate-600">
              <span className="capitalize">{progress.phase}…</span>
              <span>{progress.percent}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded bg-slate-200">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress.percent}%` }} />
            </div>
          </div>
        )}
        <DialogFooter>
          {submitting ? (
            <Button variant="outline" onClick={cancelUpload}><X className="mr-1 h-4 w-4"/>{L('Cancel','Cancelar')}</Button>
          ) : (
            <Button variant="outline" onClick={()=>onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          )}
          <Button onClick={submit} disabled={submitting} data-testid="tf4-newver-submit">{submitting?(progress?`Uploading ${progress.percent}%`:'Uploading…'):'Upload'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== Soft-delete dialog =====
function DeleteDialog({ open, onOpenChange, evidence, onDeleted }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useEffect(()=>{ if(open) setReason(''); }, [open]);
  if (!evidence) return null;
  const submit = async () => {
    if (reason.trim().length < 5) return toast.error(L('Reason ≥ 5 chars','Motivo: mínimo 5 caracteres'));
    setSubmitting(true);
    try {
      await adminApi.talentFixSoftDeleteEvidence(evidence.id, { reason });
      toast.success(L('Evidence soft-deleted','Evidencia eliminada (borrado lógico)'));
      onOpenChange(false); onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf4-delete-dialog">
        <DialogHeader><DialogTitle>{L('Soft-delete evidence','Eliminar evidencia (borrado lógico)')}</DialogTitle>
          <DialogDescription>{evidence.title} — reason required (5+ chars). Legal-hold rows refuse.</DialogDescription></DialogHeader>
        <Textarea rows={3} value={reason} onChange={(e)=>setReason(e.target.value)} data-testid="tf4-delete-reason"/>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          <Button variant="destructive" onClick={submit} disabled={submitting} data-testid="tf4-delete-submit">{submitting?'Deleting…':'Soft-delete'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== Main tab =====
export default function EvidenceTab({ caseId, isHmAdmin, planStatus }) {
  const [items, setItems] = useState([]);
  const [showAllVersions, setShowAllVersions] = useState(false);
  const [familyFilter, setFamilyFilter] = useState('all');
  const [confFilter, setConfFilter] = useState('all');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [snapOpen, setSnapOpen] = useState(false);
  const [witnessOpen, setWitnessOpen] = useState(false);
  const [confItem, setConfItem] = useState(null);
  const [lineageItem, setLineageItem] = useState(null);
  const [newVerItem, setNewVerItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  const load = async () => {
    try {
      const r = await adminApi.talentFixListEvidence(caseId, { show_all_versions: showAllVersions });
      setItems(r.items || []);
    } catch (e) { toast.error(L('Could not load evidence','No se pudo cargar la evidencia')); }
  };
  useEffect(() => { load(); }, [caseId, showAllVersions]);

  const filtered = useMemo(() => items.filter((i) =>
    (familyFilter === 'all' || i.family === familyFilter) &&
    (confFilter === 'all' || i.confidentiality_treatment === confFilter) &&
    !i.soft_deleted_at
  ), [items, familyFilter, confFilter]);

  const stage3Unlocked = planStatus === 'consent_granted';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="text-xs font-medium text-slate-600">{L('Family','Familia')}</label>
          <Select value={familyFilter} onValueChange={setFamilyFilter}>
            <SelectTrigger className="w-44" data-testid="tf4-family-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{L('All families','Todas las familias')}</SelectItem>
              <SelectItem value="document">{L('Documents','Documentos')}</SelectItem>
              <SelectItem value="media">{L('Media','Multimedia')}</SelectItem>
              <SelectItem value="communication">{L('Communication','Comunicación')}</SelectItem>
              <SelectItem value="testimony">{L('Testimony','Testimonio')}</SelectItem>
              <SelectItem value="snapshot">{L('Snapshots','Snapshots')}</SelectItem>
              <SelectItem value="external">{L('External','Externa')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">{L('Confidentiality','Confidencialidad')}</label>
          <Select value={confFilter} onValueChange={setConfFilter}>
            <SelectTrigger className="w-44" data-testid="tf4-conf-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{L('All levels','Todos los niveles')}</SelectItem>
              <SelectItem value="client_visible">{L('Client visible','Visible para el cliente')}</SelectItem>
              <SelectItem value="shared_with_client">{L('Shared with client','Compartida con el cliente')}</SelectItem>
              <SelectItem value="restricted">{L('Restricted','Restringida')}</SelectItem>
              <SelectItem value="privileged">{L('Privileged','Privilegiada')}</SelectItem>
              <SelectItem value="legal_hold">{L('Legal hold','Retención legal')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex items-end gap-2">
          <Button size="sm" variant="outline" onClick={()=>setShowAllVersions((v)=>!v)}
            data-testid="tf4-toggle-versions">{showAllVersions ? 'Hide prior versions' : 'Show all versions'}</Button>
          <Button size="sm" onClick={()=>setUploadOpen(true)} disabled={!stage3Unlocked}
            data-testid="tf4-upload-btn"><Upload className="h-4 w-4 mr-1"/>{L('Upload','Cargar')}</Button>
          <Button size="sm" variant="secondary" onClick={()=>setSnapOpen(true)} disabled={!stage3Unlocked}
            data-testid="tf4-snapshot-btn"><Camera className="h-4 w-4 mr-1"/>{L('Snapshot','Snapshot')}</Button>
          <Button size="sm" variant="secondary" onClick={()=>setWitnessOpen(true)} disabled={!stage3Unlocked}
            data-testid="tf4-witness-btn"><MessageSquare className="h-4 w-4 mr-1"/>{L('Witness','Testigo')}</Button>
        </div>
      </div>

      {!stage3Unlocked && (
        <Card className="border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="tf4-locked-card">
          <ShieldAlert className="inline h-4 w-4 mr-1"/>Stage 3 must be unlocked (plan = consent_granted) before evidence can be added.
        </Card>
      )}

      {filtered.length === 0
        ? <p className="text-sm italic text-slate-500">{L('No evidence in this view.','No hay evidencia en esta vista.')}</p>
        : filtered.map((ev) => (
            <EvidenceCard key={ev.id} ev={ev}
              onChangeConf={setConfItem}
              onSoftDelete={setDeleteItem}
              onNewVersion={setNewVerItem}
              onViewLineage={setLineageItem}/>
          ))}

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} caseId={caseId} onUploaded={load}/>
      <SnapshotDialog open={snapOpen} onOpenChange={setSnapOpen} caseId={caseId} onCaptured={load}/>
      <WitnessDialog open={witnessOpen} onOpenChange={setWitnessOpen} caseId={caseId} onCaptured={load}/>
      <ConfDialog open={!!confItem} onOpenChange={(o)=>!o&&setConfItem(null)} item={confItem}
        isHmAdmin={isHmAdmin} onChanged={()=>{ setConfItem(null); load(); }}/>
      <NewVersionDialog open={!!newVerItem} onOpenChange={(o)=>!o&&setNewVerItem(null)}
        evidence={newVerItem} onUploaded={()=>{ setNewVerItem(null); load(); }}/>
      <LineageDialog open={!!lineageItem} onOpenChange={(o)=>!o&&setLineageItem(null)} evidence={lineageItem}/>
      <DeleteDialog open={!!deleteItem} onOpenChange={(o)=>!o&&setDeleteItem(null)}
        evidence={deleteItem} onDeleted={()=>{ setDeleteItem(null); load(); }}/>
    </div>
  );
}
